export const COMMUNITY_PAYMENT_TYPES = [
  'ordinary_fee',
  'levy',
  'reserve_fund',
  'extraordinary_fee',
  'special_service_fee',
  'individual_supplies',
  'late_fee',
  'sanctions_or_liability',
  'expense_compensation',
] as const;

export type CommunityPaymentType = (typeof COMMUNITY_PAYMENT_TYPES)[number];

export const COMMUNITY_PAYMENT_TYPE_LABELS: Record<CommunityPaymentType, string> = {
  ordinary_fee: 'Cuota ordinaria',
  levy: 'Derrama',
  reserve_fund: 'Aportación al Fondo de Reserva',
  extraordinary_fee: 'Cuota extraordinaria',
  special_service_fee: 'Cuotas especial por servicios específicos',
  individual_supplies: 'Pago por suministros individualizados',
  late_fee: 'Recargo por impago (morosidad)',
  sanctions_or_liability: 'Pago extraordinario por sanciones o responsabilidades',
  expense_compensation: 'Ingresos para compensar gastos',
};

export const COMMUNITY_PAYMENT_TYPE_BADGE_CLASSES: Record<CommunityPaymentType, string> = {
  ordinary_fee: 'status-badge border-primary/25 bg-app-accent-soft text-app-accent-strong',
  levy: 'status-badge status-danger',
  reserve_fund: 'status-badge border-cyan-300/70 bg-cyan-50 text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-200',
  extraordinary_fee: 'status-badge status-warning',
  special_service_fee: 'status-badge border-indigo-300/70 bg-indigo-50 text-indigo-800 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200',
  individual_supplies: 'status-badge status-review',
  late_fee: 'status-badge status-pending',
  sanctions_or_liability: 'status-badge status-danger',
  expense_compensation: 'status-badge status-resolved',
};

export const COMMUNITY_PAYMENT_STATUSES = [
  'pending_generation',
  'pending_collection',
  'paid',
  'overdue',
  'in_default',
  'friendly_claim',
  'payment_agreement',
  'legal_claim',
  'judicialized',
  'paid_with_surcharge',
  'condoned',
  'compensated',
  'cancelled',
  'transferred',
  'regularized',
  'under_review',
] as const;

export type CommunityPaymentStatus = (typeof COMMUNITY_PAYMENT_STATUSES)[number];
export type LegacyCommunityPaymentStatus = CommunityPaymentStatus | 'pending';

export const COMMUNITY_PAYMENT_STATUS_LABELS: Record<CommunityPaymentStatus, string> = {
  pending_generation: 'Pendiente de generar',
  pending_collection: 'Pendiente de cobro',
  paid: 'Pagado',
  overdue: 'Vencido',
  in_default: 'En mora',
  friendly_claim: 'En reclamación amistosa',
  payment_agreement: 'En negociación / acuerdo de pago',
  legal_claim: 'En reclamación legal',
  judicialized: 'Judicializado',
  paid_with_surcharge: 'Pago con recargo',
  condoned: 'Condonado',
  compensated: 'Compensado',
  cancelled: 'Anulado',
  transferred: 'Traspasado',
  regularized: 'Regularizado',
  under_review: 'En revisión',
};

export const COMMUNITY_PAYMENT_STATUS_BADGE_CLASSES: Record<CommunityPaymentStatus, string> = {
  pending_generation: 'status-badge status-discarded',
  pending_collection: 'status-badge status-pending',
  paid: 'status-badge status-resolved',
  overdue: 'status-badge status-warning',
  in_default: 'status-badge status-danger',
  friendly_claim: 'status-badge status-pending',
  payment_agreement: 'status-badge status-info',
  legal_claim: 'status-badge status-danger',
  judicialized: 'status-badge border-fuchsia-300/70 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-400/30 dark:bg-fuchsia-400/10 dark:text-fuchsia-200',
  paid_with_surcharge: 'status-badge border-lime-300/70 bg-lime-50 text-lime-800 dark:border-lime-400/30 dark:bg-lime-400/10 dark:text-lime-200',
  condoned: 'status-badge border-teal-300/70 bg-teal-50 text-teal-800 dark:border-teal-400/30 dark:bg-teal-400/10 dark:text-teal-200',
  compensated: 'status-badge status-resolved',
  cancelled: 'status-badge status-discarded',
  transferred: 'status-badge status-info',
  regularized: 'status-badge border-cyan-300/70 bg-cyan-50 text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-200',
  under_review: 'status-badge status-review',
};

export type CommunityPaymentLifecycleGroup = 'pending' | 'completed';

export const COMMUNITY_PAYMENT_STATUS_GROUPS: Record<CommunityPaymentStatus, CommunityPaymentLifecycleGroup> = {
  pending_generation: 'pending',
  pending_collection: 'pending',
  paid: 'completed',
  overdue: 'pending',
  in_default: 'pending',
  friendly_claim: 'pending',
  payment_agreement: 'pending',
  legal_claim: 'pending',
  judicialized: 'pending',
  paid_with_surcharge: 'completed',
  condoned: 'completed',
  compensated: 'completed',
  cancelled: 'completed',
  transferred: 'completed',
  regularized: 'completed',
  under_review: 'pending',
};

export const resolvePaymentLifecycleGroup = (
  status?: LegacyCommunityPaymentStatus | null,
): CommunityPaymentLifecycleGroup => {
  if (status === 'pending') {
    return 'pending';
  }
  if (status && status in COMMUNITY_PAYMENT_STATUS_GROUPS) {
    return COMMUNITY_PAYMENT_STATUS_GROUPS[status as CommunityPaymentStatus];
  }
  return 'pending';
};
